import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { CreateHoliday, HolidayRecord, PhaseDeadline } from '@evogard/contract';
import {
  loadFailureMessage,
  type ClaimDepartment,
  type SlaDayKind,
  type SlaDepartment,
} from '@evogard/domain';
import { api, ok } from '../../../lib/api.js';
import { can, useSessionContext } from '../../../lib/session.js';
import { notifyClaimChanged } from './useDesk.js';

/**
 * Os prazos do sinistro na interna (07/10/2026): o vencimento do card no
 * departamento e o SLA de cada fase. A raiz é a mesma de `useClaims.ts`:
 * gravar o vencimento muda a ficha, a lista e a Atividade de uma vez.
 */

const ROOT = ['sinistros', 'sinistro'] as const;

function useTenantId() {
  const { data: context } = useSessionContext();
  return context?.currentTenant.id;
}

export function useDeadlinePermissions() {
  const { data: context } = useSessionContext();
  return {
    canSetDueDate: can(context, 'claims.deadlines', 'editar'),
    canConfigure: can(context, 'claims.deadlines', 'configurar'),
    /* Os feriados são da plataforma (15/10/2026), com recurso próprio. */
    canEditHolidays: can(context, 'platform.holidays', 'editar'),
  };
}

/** O SLA de cada fase da base — toda etapa de trabalho, com nulo quando não tem. */
export function usePhaseDeadlines() {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: [...ROOT, 'sla', tenantId],
    queryFn: async (): Promise<PhaseDeadline[]> => {
      const r = await api.claims.listPhaseDeadlines();
      if (r.status !== 200)
        throw new Error(loadFailureMessage(r.status, 'o SLA das fases'));
      return r.body;
    },
    enabled: tenantId !== undefined,
  });
}

export function useSetPhaseDeadlines() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (body: {
      department: SlaDepartment;
      phases: { phase: string; deadlineHours: number | null; dayKind: SlaDayKind }[];
    }) =>
      ok(
        await api.claims.setPhaseDeadlines({ body }),
        200,
        'Não foi possível gravar o SLA das fases.',
      ),
    onSuccess: () => client.invalidateQueries({ queryKey: [...ROOT, 'sla'] }),
  });
}

/**
 * Quantos cards de cada fase estão com o SLA expirado — o selo do cabeçalho
 * da coluna. `version` sobe a cada ato da mesa, como o quadro.
 */
export function useSlaSummary(department: SlaDepartment, version: number) {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: [...ROOT, 'sla-resumo', tenantId, department, version],
    queryFn: async () => {
      const r = await api.claims.slaSummary({ query: { department } });
      if (r.status !== 200) throw new Error(loadFailureMessage(r.status, 'os SLAs'));
      return r.body;
    },
    enabled: tenantId !== undefined,
  });
}

/** Define, troca ou tira o vencimento do card no departamento. O quadro acompanha. */
export function useSetDueDate(claimId: number) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (body: { department: ClaimDepartment; dueAt: string | null }) =>
      ok(
        await api.claims.setClaimDueDate({ params: { id: claimId }, body }),
        200,
        'Não foi possível gravar o vencimento.',
      ),
    onSettled: () => {
      notifyClaimChanged(claimId);
      return client.invalidateQueries({ queryKey: [...ROOT] });
    },
  });
}

/*
 * Os feriados da praça (15/10/2026). A tabela e as rotas são da plataforma
 * (`/platform/holidays`); a tela do cadastro mora, por ora, no SLA das fases.
 * Gravar invalida o SLA também: a prévia "quem entrar agora expira" conta com
 * eles.
 */
const HOLIDAYS = ['plataforma', 'feriados'] as const;

export function useHolidays() {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: [...HOLIDAYS, tenantId],
    queryFn: async (): Promise<HolidayRecord[]> => {
      const r = await api.holidays.listHolidays();
      if (r.status !== 200) throw new Error(loadFailureMessage(r.status, 'os feriados'));
      return r.body;
    },
    enabled: tenantId !== undefined,
  });
}

export function useHolidayMutations() {
  const client = useQueryClient();
  const changed = () => client.invalidateQueries({ queryKey: HOLIDAYS });
  return {
    create: useMutation({
      mutationFn: async (body: CreateHoliday) =>
        ok(
          await api.holidays.createHoliday({ body }),
          201,
          'Não foi possível cadastrar o feriado.',
        ),
      onSuccess: changed,
    }),
    remove: useMutation({
      mutationFn: async (id: string) =>
        ok(
          await api.holidays.removeHoliday({ params: { id }, body: {} }),
          200,
          'Não foi possível remover o feriado.',
        ),
      onSuccess: changed,
    }),
  };
}
